import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Modal,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

import EmpleadosCargaMasivaPreview from "./EmpleadosCargaMasivaPreview";

import { previewImportEmpleadosCsv } from "../api/empleadosApi";

const REQUIRED_COLUMNS = [
  "full_name",
  "email",
  "dni",
  "branch_names",
  "area_names",
  "role_names",
];

const OPTIONAL_COLUMNS = [
  "cuit",
  "phone_number",
  "birth_date",
  "marital_status",
  "children_count",
  "education_level",
  "health",
  "nationality",
  "province",
  "locality",
  "neighborhood",
  "address",
  "postal_code",
  "contract_name",
  "hire_date",
  "status",
  "contract_end_date",
  "salary_mode",
  "base_salary",
  "hourly_rate",
  "cvu",
  "bank",
  "account_alias",
  "payroll_notes",
];

function buildTemplateCsv() {
  const headers = [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS];

  const row1 = [
    "Juan Pérez",
    "juan@empresa.com",
    "20345678",
    "Sucursal Central|Sucursal Norte",
    "Cocina|Administración",
    "Encargado",
    "20-20345678-9",
    "1133445566",
    "1990-05-15",
    "SINGLE",
    "0",
    "SECONDARY",
    "",
    "Argentina",
    "Buenos Aires",
    "CABA",
    "Palermo",
    "Av. Siempre Viva 742",
    "1425",
    "FORMAL",
    "2026-03-01",
    "ACTIVE",
    "",
    "MENSUAL",
    "150000.00",
    "",
    "0000003100012345678901",
    "Banco Nación",
    "juan.perez.mp",
    "",
  ];

  const row2 = [
    "Ana López",
    "ana@empresa.com",
    "30111222",
    "Sucursal Central",
    "Administración",
    "Cajera|Recepcionista",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "INFORMAL",
    "2026-03-05",
    "ACTIVE",
    "",
    "HORA",
    "",
    "850.50",
    "",
    "",
    "",
    "",
  ];

  return [headers.join(","), row1.join(","), row2.join(",")].join("\n");
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function Pill({ children, sx }) {
  return (
    <Box
      sx={{
        px: 2.2,
        py: 1,
        bgcolor: "secondary.200",
        borderRadius: "33px",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function InfoCard({ title, children }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        bgcolor: "rgba(247, 247, 248, 1)",
        borderRadius: "20px",
        p: 2,
        boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.35)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 700,
          color: "primary.900",
          mb: 1.2,
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default function EmpleadosCargaMasivaModal({
  open,
  onClose,
  onConfirm,
}) {
  const inputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [previewState, setPreviewState] = useState({
    loading: false,
    hasErrors: false,
    fileError: "",
    totalRows: 0,
    validRows: 0,
    invalidRows: 0,
    rows: [],
  });
  const [importing, setImporting] = useState(false);

  const templateCsv = useMemo(() => buildTemplateCsv(), []);

  function resetState() {
    setSelectedFile(null);
    setDragActive(false);
    setErrorMsg("");
    setPreviewState({
      loading: false,
      hasErrors: false,
      fileError: "",
      totalRows: 0,
      validRows: 0,
      invalidRows: 0,
      rows: [],
    });
    if (inputRef.current) inputRef.current.value = "";
  }

  function handleClose() {
    resetState();
    onClose?.();
  }

  async function validateAndSetFile(file) {
    setErrorMsg("");

    if (!file) return;

    const isCsv =
      file.type === "text/csv" ||
      file.name.toLowerCase().endsWith(".csv");

    if (!isCsv) {
      setSelectedFile(null);
      setErrorMsg("El archivo debe ser un .csv");
      return;
    }

    setSelectedFile(file);
    await runPreview(file);
  }

  function handleInputChange(event) {
    const file = event.target.files?.[0];
    validateAndSetFile(file);
  }

  function handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);

    const file = event.dataTransfer?.files?.[0];
    validateAndSetFile(file);
  }

  function handleDownloadTemplate() {
    const blob = new Blob([templateCsv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "plantilla_empleados.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
  }

  async function runPreview(file) {
    setPreviewState({
      loading: true,
      hasErrors: false,
      fileError: "",
      totalRows: 0,
      validRows: 0,
      invalidRows: 0,
      rows: [],
    });

    try {
      const res = await previewImportEmpleadosCsv(file);
      const data = res?.data ?? res;

      setPreviewState({
        loading: false,
        hasErrors: (data?.summary?.invalidRows ?? 0) > 0,
        fileError: "",
        totalRows: data?.summary?.totalRows ?? 0,
        validRows: data?.summary?.validRows ?? 0,
        invalidRows: data?.summary?.invalidRows ?? 0,
        rows: data?.rows ?? [],
      });
    } catch (err) {
      setPreviewState({
        loading: false,
        hasErrors: true,
        fileError:
          err?.response?.data?.message ||
          err?.response?.data?.detail ||
          err?.message ||
          "No se pudo previsualizar el archivo.",
        totalRows: 0,
        validRows: 0,
        invalidRows: 0,
        rows: [],
      });
    }
  }

  async function handleConfirm() {
    if (!selectedFile) {
      setErrorMsg("Primero seleccioná un archivo CSV.");
      return;
    }

    if (previewState.loading) {
      setErrorMsg("Esperá a que termine la validación del archivo.");
      return;
    }

    if (previewState.hasErrors) {
      setErrorMsg("Hay filas inválidas. Corregí el CSV antes de continuar.");
      return;
    }

    if (!previewState.totalRows) {
      setErrorMsg("No se encontraron filas válidas para importar.");
      return;
    }

    setErrorMsg("");
    setImporting(true);

    try {
      await onConfirm?.(selectedFile);
      resetState();
      onClose?.();
    } catch (err) {
      setErrorMsg(
        err?.data?.message ||
        err?.message ||
        "No se pudo importar el archivo."
      );
    } finally {
      setImporting(false);
    }
  }

  useEffect(() => {
    if (!open) {
      resetState();
      setImporting(false);
    }
  }, [open]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          width: "100vw",
          height: "100vh",
          overflowY: "auto",
          backgroundColor: "rgba(242, 243, 249, 0.96)",
          outline: "none",
          position: "relative",
          py: 4,
          px: { xs: 2, md: 3 },
        }}
      >
        <IconButton
          onClick={handleClose}
          disabled={importing}
          sx={{
            position: "absolute",
            top: 16,
            right: 20,
            color: "rgba(2, 8, 23, 0.7)",
            zIndex: 3,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <Stack spacing={0.75} sx={{ alignItems: "center", pt: 1 }}>
            <Pill>
              <Stack direction="row" spacing={1} alignItems="center">
                <GroupAddIcon sx={{ fontSize: 20, color: "primary.900" }} />
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "primary.900",
                  }}
                >
                  Carga masiva de empleados
                </Typography>
              </Stack>
            </Pill>
          </Stack>

          <Box
            sx={{
              width: "100%",
              maxWidth: "1080px",
              bgcolor: "neutral.300",
              borderRadius: "30px",
              boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.5)",
              p: { xs: 2, md: 3 },
            }}
          >
            <Stack spacing={2.2}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.2}
                sx={{
                  justifyContent: "space-between",
                  alignItems: { xs: "stretch", md: "center" },
                }}
              >
                <Stack spacing={0.4}>
                  <Typography sx={{ fontSize: 18, fontWeight: 700, color: "primary.900" }}>
                    Archivo CSV
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "rgba(2, 8, 23, 0.75)" }}>
                    Recomendado: descargar la plantilla y completar una fila por empleado.
                  </Typography>
                </Stack>

                <Button
                  onClick={handleDownloadTemplate}
                  startIcon={<DownloadIcon />}
                  sx={{
                    alignSelf: { xs: "flex-start", md: "auto" },
                    bgcolor: "rgba(216, 220, 237, 1)",
                    color: "primary.900",
                    px: 2,
                    py: 1,
                    borderRadius: "33px",
                    textTransform: "none",
                    fontWeight: 700,
                    "&:hover": { bgcolor: "rgba(191, 198, 225, 1)" },
                  }}
                >
                  Descargar plantilla
                </Button>
              </Stack>

              {!selectedFile && (
                <Box
                  onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragActive(true);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragActive(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragActive(false);
                  }}
                  onDrop={handleDrop}
                  sx={{
                    border: dragActive
                      ? "2px dashed rgba(14, 50, 149, 1)"
                      : "2px dashed rgba(117, 117, 138, 0.55)",
                    bgcolor: dragActive
                      ? "rgba(230, 237, 255, 0.7)"
                      : "rgba(255,255,255,0.55)",
                    borderRadius: "24px",
                    minHeight: 220,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all .2s ease",
                    px: 3,
                  }}
                >
                  <Stack spacing={1.5} sx={{ alignItems: "center", textAlign: "center" }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "18px",
                        bgcolor: "rgba(216, 220, 237, 1)",
                        display: "grid",
                        placeItems: "center",
                        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
                      }}
                    >
                      <UploadFileIcon sx={{ fontSize: 34, color: "primary.900" }} />
                    </Box>

                    <Stack spacing={0.5}>
                      <Typography sx={{ fontSize: 17, fontWeight: 700, color: "primary.900" }}>
                        Arrastrá tu CSV aquí
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: "rgba(2, 8, 23, 0.75)" }}>
                        o seleccioná un archivo desde tu computadora
                      </Typography>
                    </Stack>

                    <input
                      ref={inputRef}
                      type="file"
                      accept=".csv,text/csv"
                      style={{ display: "none" }}
                      onChange={handleInputChange}
                    />

                    <Button
                      onClick={() => inputRef.current?.click()}
                      startIcon={<DescriptionOutlinedIcon />}
                      sx={{
                        bgcolor: "secondary.500",
                        color: "logoblanco",
                        px: 2.5,
                        py: 1,
                        borderRadius: "33px",
                        textTransform: "none",
                        fontWeight: 700,
                        "&:hover": { bgcolor: "secondary.600" },
                      }}
                    >
                      Seleccionar archivo
                    </Button>

                    <Typography sx={{ fontSize: 12, color: "rgba(2, 8, 23, 0.65)" }}>
                      Formato admitido: .csv
                    </Typography>
                  </Stack>
                </Box>
              )}

              {selectedFile && (
                <>
                  <Box
                    sx={{
                      bgcolor: "rgba(247, 247, 248, 1)",
                      borderRadius: "20px",
                      p: 2,
                      boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.35)",
                    }}
                  >
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={1.5}
                      sx={{
                        justifyContent: "space-between",
                        alignItems: { xs: "flex-start", md: "center" },
                      }}
                    >
                      <Stack spacing={0.4}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.900" }}>
                          Archivo seleccionado
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "rgba(2, 8, 23, 0.8)" }}>
                          {selectedFile.name}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "rgba(2, 8, 23, 0.65)" }}>
                          Tamaño: {formatBytes(selectedFile.size)}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={1}>
                        <Button
                          onClick={() => inputRef.current?.click()}
                          disabled={importing}
                          sx={{
                            bgcolor: "rgba(216, 220, 237, 1)",
                            color: "primary.900",
                            px: 2,
                            py: 1,
                            borderRadius: "33px",
                            textTransform: "none",
                            fontWeight: 700,
                            "&:hover": { bgcolor: "rgba(191, 198, 225, 1)" },
                          }}
                        >
                          Cambiar archivo
                        </Button>

                        <Button
                          onClick={resetState}
                          disabled={importing}
                          sx={{
                            bgcolor: "rgba(216, 220, 237, 1)",
                            color: "primary.900",
                            px: 2,
                            py: 1,
                            borderRadius: "33px",
                            textTransform: "none",
                            fontWeight: 700,
                            "&:hover": { bgcolor: "rgba(191, 198, 225, 1)" },
                          }}
                        >
                          Quitar archivo
                        </Button>
                      </Stack>
                    </Stack>

                    <input
                      ref={inputRef}
                      type="file"
                      accept=".csv,text/csv"
                      style={{ display: "none" }}
                      onChange={handleInputChange}
                    />
                  </Box>

                  <EmpleadosCargaMasivaPreview
                    loading={previewState.loading}
                    fileError={previewState.fileError}
                    rows={previewState.rows}
                    totalRows={previewState.totalRows}
                    validRows={previewState.validRows}
                    invalidRows={previewState.invalidRows}
                  />
                </>
              )}

              <Stack
                direction={{ xs: "column", lg: "row" }}
                spacing={2}
                sx={{ alignItems: "stretch" }}
              >
                <InfoCard title="Reglas del archivo">
                  <Stack spacing={1}>
                    <Typography sx={{ fontSize: 13, color: "rgba(2, 8, 23, 0.8)" }}>
                      • Las columnas opcionales pueden estar presentes y venir vacías.
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "rgba(2, 8, 23, 0.8)" }}>
                      • Para múltiples sucursales, áreas o roles dentro de una celda, usar <b>|</b>.
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "rgba(2, 8, 23, 0.8)" }}>
                      • Fechas recomendadas en formato <b>YYYY-MM-DD</b>.
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "rgba(2, 8, 23, 0.8)" }}>
                      • Estados sugeridos: <b>ACTIVE</b> o <b>INACTIVE</b>.
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "rgba(2, 8, 23, 0.8)" }}>
                      • Modo de salario: <b>MENSUAL</b> (usa base_salary) o <b>HORA</b> (usa hourly_rate).
                    </Typography>
                  </Stack>
                </InfoCard>

                <InfoCard title="Columnas obligatorias">
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {REQUIRED_COLUMNS.map((col) => (
                      <Chip
                        key={col}
                        label={col}
                        sx={{
                          bgcolor: "rgba(216, 220, 237, 1)",
                          color: "primary.900",
                          fontWeight: 700,
                        }}
                      />
                    ))}
                  </Stack>
                </InfoCard>
              </Stack>

              {errorMsg && (
                <Alert severity="error" sx={{ borderRadius: "14px" }}>
                  {errorMsg}
                </Alert>
              )}

              {selectedFile && previewState.hasErrors && !previewState.loading && !previewState.fileError && (
                <Alert severity="warning" sx={{ borderRadius: "14px" }}>
                  Hay filas inválidas en el archivo. Corregilas antes de continuar.
                </Alert>
              )}

              <Stack
                direction="row"
                spacing={1.2}
                sx={{ justifyContent: "center", flexWrap: "wrap", pt: 0.5 }}
              >
                <Button
                  onClick={handleClose}
                  disabled={importing}
                  sx={{
                    bgcolor: "rgba(216, 220, 237, 1)",
                    color: "primary.900",
                    px: 2.5,
                    py: 1,
                    borderRadius: "33px",
                    textTransform: "none",
                    fontWeight: 700,
                    "&:hover": { bgcolor: "rgba(191, 198, 225, 1)" },
                  }}
                >
                  Cancelar
                </Button>

                <Button
                  onClick={handleConfirm}
                  startIcon={
                    importing ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <GroupAddIcon />
                  }
                  disabled={
                    importing ||
                    !selectedFile ||
                    previewState.loading ||
                    previewState.hasErrors ||
                    !!previewState.fileError ||
                    previewState.totalRows === 0
                  }
                  sx={{
                    bgcolor: "secondary.500",
                    color: "logoblanco",
                    px: 2.8,
                    py: 1,
                    borderRadius: "33px",
                    textTransform: "none",
                    fontWeight: 700,
                    "&.Mui-disabled": {
                      bgcolor: "rgba(165, 165, 178, 0.5)",
                      color: "rgba(255,255,255,0.8)",
                    },
                    "&:hover": { bgcolor: "secondary.600" },
                  }}
                >
                  {importing ? "Importando..." : "Continuar con importación"}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
}